import { useEffect, useRef, useState, useCallback } from "react";
import httpService from "../services/httpService";
import { useAudioPlayerStore } from "../store/store";
import {
    FaPlay,
    FaPause,
    FaTimes,
    FaChevronUp,
    FaChevronDown,
    FaVolumeUp,
    FaVolumeMute,
    FaItunesNote
} from "react-icons/fa";
import "../styles/GlobalAudioPlayer.scss";

const fmt = (s) => {
    if (isNaN(s) || s == null) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
};

const GlobalAudioPlayer = () => {
    const { audioUrl, isPlaying, from, featuredImage, episodeId, setPlay, setPause, setAudioUrl, pendingSeek, clearPendingSeek, setCurrentTime: setStoreCurrentTime, setDuration: setStoreDuration } = useAudioPlayerStore();
    const prevFrom = useRef(null);
    const audioRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [history, setHistory] = useState([]);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);

    const fetchHistory = () => {
        httpService.get('dash/audio-player/history')
            .then(res => setHistory(res.data.data ?? []))
            .catch(() => {});
    };

    useEffect(() => {
        if (from && from !== prevFrom.current) {
            setVisible(true);
        }
        prevFrom.current = from;
    }, [from]);

    // Fetch history on mount
    useEffect(() => {
        fetchHistory();
    }, []);

    // POST new history record when audioUrl changes, then refresh history
    useEffect(() => {
        if (!audioUrl) return;
        httpService.post('dash/audio-player/history', {
            audio_url: audioUrl,
            episode_id: episodeId ?? null,
            listened_time: audioRef.current?.currentTime ?? 0,
        }).then(() => fetchHistory()).catch(() => {});
    }, [audioUrl]);

    // POST updated listened_time every 30s while playing
    useEffect(() => {
        if (!isPlaying || !audioUrl) return;
        const interval = setInterval(() => {
            httpService.post('dash/audio-player/history', {
                audio_url: audioUrl,
                episode_id: episodeId ?? null,
                listened_time: Math.floor(audioRef.current?.currentTime ?? 0),
            }).catch(() => {});
        }, 30000);
        return () => clearInterval(interval);
    }, [isPlaying, audioUrl]);

    useEffect(() => {
        if (!audioRef.current) return;
        if (isPlaying) {
            setVisible(true);
            const timer = setTimeout(() => {
                audioRef.current?.play().catch(() => setPause());
            }, 1500);
            return () => clearTimeout(timer);
        } else {
            audioRef.current.pause();
        }
    }, [isPlaying]);

    useEffect(() => {
        console.log('in use effect global audio player')
        if (!audioRef.current || !audioUrl) return;
        audioRef.current.load();
        audioRef.current.play().catch(() => setPause());
        setVisible(true);
        setCurrentTime(0);
        setDuration(0);
        setStoreCurrentTime(0);
        setStoreDuration(0);
        setPlay();
    }, [audioUrl]);

    useEffect(() => {
        if (pendingSeek === null || !audioRef.current) return;
        const doSeek = () => {
            audioRef.current.currentTime = pendingSeek;
            clearPendingSeek();
        };
        if (audioRef.current.readyState >= 1) {
            doSeek();
        } else {
            audioRef.current.addEventListener('loadedmetadata', doSeek, { once: true });
        }
    }, [pendingSeek]);

const onTimeUpdate = () => {
        const t = audioRef.current?.currentTime ?? 0;
        setCurrentTime(t);
        setStoreCurrentTime(t);
    };
    const onLoadedMetadata = () => {
        const d = audioRef.current?.duration ?? 0;
        setDuration(d);
        setStoreDuration(d);
    };

    const handleSeek = (e) => {
        const val = Number(e.target.value);
        audioRef.current.currentTime = val;
        setCurrentTime(val);
    };

    const handleVolume = (e) => {
        const val = Number(e.target.value);
        setVolume(val);
        audioRef.current.volume = val;
        setMuted(val === 0);
    };

    const toggleMute = () => {
        const next = !muted;
        setMuted(next);
        audioRef.current.muted = next;
    };

    if (!audioUrl) return null;

    return (
        <div className={`gap-wrapper${visible ? '' : ' gap-wrapper--hidden'}`}>
            <button className="gap-toggle-btn" onClick={() => setVisible(v => !v)}>
                <FaItunesNote /> &nbsp; {!visible ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            </button>
            <div className="global-audio-player">
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onTimeUpdate={onTimeUpdate}
                    onLoadedMetadata={onLoadedMetadata}
                    onEnded={() => setPause()}
                />

                <div className="gap-close-row">
                    {/*<button className="gap-close-btn" onClick={() => setAudioUrl(null)}>*/}
                    {/*    <FaTimes size={12} />*/}
                    {/*</button>*/}
                </div>

                {featuredImage && (
                    <img className="gap-featured-image" src={featuredImage} alt="" />
                )}

                <div className="gap-title"><span>{from || 'Now playing'}</span></div>

                <button className="gap-play-btn" onClick={() => isPlaying ? setPause() : setPlay()}>
                    {isPlaying ? <FaPause size={14} /> : <FaPlay size={14} />}
                </button>

                <div className="gap-time">{fmt(currentTime)} / {fmt(duration)}</div>

                <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSeek}
                />

                <div className="gap-volume">
                    <button className="gap-volume-btn" onClick={toggleMute}>
                        {muted || volume === 0 ? <FaVolumeMute size={13} /> : <FaVolumeUp size={13} />}
                    </button>
                    <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={muted ? 0 : volume}
                        onChange={handleVolume}
                    />
                </div>
            </div>
        </div>
    );
};

export default GlobalAudioPlayer;
